import { useCallback, useMemo, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import {
  AllDoneFill,
  ArrowBackOutline,
  Calendar2Outline,
  DepartmentOutline,
  EditOutline,
  PeopleGroupOutline,
  PinOutline,
} from '@lyearn/icons';
import { Avatar, Button, DotSeparator, Loader, Stack, Tabs, Tooltip, Typography } from '@lyearn/ui';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import useNavigation from '@/hooks/useNavigation';
import { useQuestionsQuery } from '@/modules/corporate/modules/questions/views/AddQuestionModal/graphql/queries/generated/questions';
import TemplatesHeaderNode from '@/modules/templates/views/components/TemplatesHeaderNode';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import {
  ContentApprovalStatusEnum,
  KnowledgeAssetContentType,
  UserSurveyResponseVersionStatusEnum,
} from '@/types/schema';

import { useKnowledgeAssetsQuery } from '../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';
import { logoStyle } from '../../KnowledgeAssetManager/styles';
import Disclaimer from '../components/Disclaimer';
import { usePersonaQuery } from '../graphql/queries/generated/persona';
import { usePersonaDetailsQuery } from '../graphql/queries/generated/personaDetails';
import { usePersonaQuestionIdsQuery } from '../graphql/queries/generated/personaQuestionIds';
import useApproveContent from '../hooks/useApproveContent';
import { getResponseByQuestionText } from '../SolutionDetails/utils';
import { DetailsPageProps } from '../types';
import BusinessImpacts from './tabs/BusinessImpacts';
import CompetitiveAnalysis from './tabs/CompetitiveAnalysis';
import CustomerInsights from './tabs/CustomerInsights';
import Documentations from './tabs/Documentations';
import SolutionOverview from './tabs/SolutionOverview';

enum SolutionWinTab {
  SOLUTION_OVERVIEW = 'solution_overview',
  CX_ARCHITECTURE = 'cx_architecture',
  COMPETITIVE_ANALYSIS = 'competitive_analysis',
  CUSTOMER_INSIGHTS = 'customer_insights',
  COLLATERAL = 'collateral',
}

const SolutionWinDetails = ({ assetId, isPreview }: DetailsPageProps) => {
  const { isMobile } = useScreen();
  const [currentTab, setCurrentTab] = useState<SolutionWinTab>(SolutionWinTab.SOLUTION_OVERVIEW);
  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();

  const { onApprove, fetching: isApproving } = useApproveContent({ contentId: assetId });

  const [{ data: personaData, fetching: fetchingPersonaData }] = usePersonaQuery({
    variables: {
      filters: {
        id: assetId,
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const showApproveButton =
    (personaData?.content as KnowledgeAssetContentType)?.approvalStatus ===
    ContentApprovalStatusEnum.InReview;

  const attemptIds =
    (personaData?.content as any)?.userSurveyAttemptConfig?.map(
      (attempt: any) => attempt?.attemptId,
    ) || [];

  const userSurveyIds = (personaData?.content as any)?.userSurveyAttemptConfig?.map(
    (attempt: any) => attempt?.userSurveyId,
  );

  const [{ data: personaDetailsData, fetching: fetchingPersonaDetailsData }] =
    usePersonaDetailsQuery({
      variables: {
        filters: {
          attemptIds,
          ...(isPreview ? { versionStatus: UserSurveyResponseVersionStatusEnum.Draft } : {}),
        },
        first: -1,
      },
      pause: !attemptIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const [{ data: personaQuestionsData, fetching: fetchingUserSurveyDetailsData }] =
    usePersonaQuestionIdsQuery({
      variables: {
        filters: {
          ids: userSurveyIds,
        },
      },
      pause: !userSurveyIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const questionIds =
    personaQuestionsData?.userSurveys?.edges?.flatMap(
      (edge) =>
        edge?.node?.questionConfig?.surveyQuestions
          ?.map((question) => question?.questionId)
          .filter((id): id is string => !!id) || [],
    ) || [];

  const [{ data: questionsData, fetching: fetchingQuestionsData }] = useQuestionsQuery({
    variables: {
      filters: {
        ids: questionIds,
      },
    },
    pause: !questionIds?.length,
    requestPolicy: 'cache-and-network',
  });

  const isLoading =
    fetchingPersonaData ||
    fetchingPersonaDetailsData ||
    fetchingUserSurveyDetailsData ||
    fetchingQuestionsData;

  const pageTitle = getResponseByQuestionText('Customer name', questionsData, personaDetailsData);
  const description = getResponseByQuestionText('Description', questionsData, personaDetailsData);
  const team = getResponseByQuestionText('Team Members', questionsData, personaDetailsData);
  const teamMembers = team
    ? team
        .split('|')
        .map((name) => name.trim())
        .filter(Boolean)
    : [];

  const teamMembersTooltipText = team
    ?.split('|')
    .map((name) => name.trim())
    .filter(Boolean)
    .join(', ');

  const formattedTeam = useMemo(() => {
    if (!teamMembers?.length) return 'N/A';

    const firstMember = teamMembers[0].split(' ')[0];
    const truncatedName = firstMember.length > 10 ? `${firstMember.slice(0, 10)}...` : firstMember;
    const remainingCount = teamMembers.length - 1;

    return (
      <Stack alignItems="center" gapX={8}>
        <Typography className="text-text-secondary" variant="body-short-02">
          {truncatedName}
          {remainingCount > 0 ? `... +${remainingCount}` : ''}
        </Typography>
      </Stack>
    );
  }, [teamMembers]);

  const industry = getResponseByQuestionText('Industry', questionsData, personaDetailsData);
  const location = getResponseByQuestionText(
    'Geographic Location',
    questionsData,
    personaDetailsData,
  );

  const customerFeedback = getResponseByQuestionText(
    'Customer Feedback',
    questionsData,
    personaDetailsData,
  );
  const lessonsLearned = getResponseByQuestionText(
    'Lessons Learned',
    questionsData,
    personaDetailsData,
  );
  const partnerships =
    getResponseByQuestionText('Partnerships', questionsData, personaDetailsData)
      ?.split('|')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const integrations =
    getResponseByQuestionText('Integrations', questionsData, personaDetailsData)
      ?.split('|')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const competitors = getResponseByQuestionText('Competitors', questionsData, personaDetailsData);
  const differentiators = getResponseByQuestionText(
    'What were our differentiators?',
    questionsData,
    personaDetailsData,
  );
  const winReasons = getResponseByQuestionText(
    'Why did we win?',
    questionsData,
    personaDetailsData,
  );

  const solutionAssets = getResponseByQuestionText(
    'Link To Solution Assets',
    questionsData,
    personaDetailsData,
  );
  const cxLinksRaw = getResponseByQuestionText('URL', questionsData, personaDetailsData);
  const uploadedImages = getResponseByQuestionText(
    'Upload Image',
    questionsData,
    personaDetailsData,
  );
  const solutionWinImage = getResponseByQuestionText(
    'Solution Win Image',
    questionsData,
    personaDetailsData,
  );
  const blueprintImagesRaw = getResponseByQuestionText(
    'Blueprint',
    questionsData,
    personaDetailsData,
  );
  const solutionWinSalesforceLinksCombined = getResponseByQuestionText(
    'Salesforce Opportunities',
    questionsData,
    personaDetailsData,
  );
  const solutionWinSalesforceLinks = solutionWinSalesforceLinksCombined
    ? solutionWinSalesforceLinksCombined
        .split('|')
        .map((link) => link.trim())
        .filter(Boolean)
    : [];

  const links = solutionAssets
    ? solutionAssets
        .split('|')
        .map((link) => link.trim())
        .filter(Boolean)
    : [];
  const cxLinks = cxLinksRaw
    ? cxLinksRaw
        .split('|')
        .map((link) => link.trim())
        .filter(Boolean)
    : [];
  const images = [
    ...(solutionWinImage
      ? solutionWinImage
          .split('|')
          .map((image) => image.trim())
          .filter(Boolean)
      : []),
    ...(uploadedImages
      ? uploadedImages
          .split('|')
          .map((image) => image.trim())
          .filter(Boolean)
      : []),
  ];
  const blueprintImages = [
    ...(blueprintImagesRaw
      ? blueprintImagesRaw
          .split('|')
          .map((image) => image.trim())
          .filter(Boolean)
      : []),
  ];

  const painSolved =
    getResponseByQuestionText('Pain Solved', questionsData, personaDetailsData)
      ?.split('|')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const productsIncluded =
    getResponseByQuestionText('Products Included', questionsData, personaDetailsData)
      ?.split(',')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const aiCapabilities =
    getResponseByQuestionText('AI Capabilities', questionsData, personaDetailsData)
      ?.split(',')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const predictedTime =
    getResponseByQuestionText('Predicted Time', questionsData, personaDetailsData) || '';
  const actualTime =
    getResponseByQuestionText('Actual Time', questionsData, personaDetailsData) || '';

  const productServiceFocus = getResponseByQuestionText(
    'Product or Service Focus',
    questionsData,
    personaDetailsData,
  );
  const geographicPresence = getResponseByQuestionText(
    'Geographic Presence',
    questionsData,
    personaDetailsData,
  );
  const marketPositionSize = getResponseByQuestionText(
    'Market Position',
    questionsData,
    personaDetailsData,
  );
  const sizeAndScale = getResponseByQuestionText(
    'Size And Scale',
    questionsData,
    personaDetailsData,
  );

  const solutionType =
    getResponseByQuestionText('Solution Type', questionsData, personaDetailsData)
      ?.split('|')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const solutionConversation =
    getResponseByQuestionText('Solution Conversation', questionsData, personaDetailsData)
      ?.split('|')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const solutionCategories =
    getResponseByQuestionText('Solution Categories', questionsData, personaDetailsData)
      ?.split('|')
      .map((item) => item.trim())
      .filter(Boolean) || [];

  const companyLogo = getResponseByQuestionText('Company Logo', questionsData, personaDetailsData);

  const solutionResponse = getResponseByQuestionText(
    'Add solution',
    questionsData,
    personaDetailsData,
  );
  const solutionIds = solutionResponse
    ? solutionResponse
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

  const [{ data: solutionsData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: solutionIds,
      },
    },
    pause: !solutionIds.length,
    requestPolicy: 'cache-and-network',
  });

  const solutions = (solutionsData?.contents?.edges || []) as Array<{
    node: KnowledgeAssetContentType;
  }>;

  const salesConversationResponse = getResponseByQuestionText(
    'Sales Conversation',
    questionsData,
    personaDetailsData,
  );
  const salesConversationIds = salesConversationResponse
    ? salesConversationResponse
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

  const [{ data: salesConversationsData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: salesConversationIds,
      },
    },
    pause: !salesConversationIds.length,
    requestPolicy: 'cache-and-network',
  });

  const salesConversations = (salesConversationsData?.contents?.edges || []) as Array<{
    node: KnowledgeAssetContentType;
  }>;

  const startDate = getResponseByQuestionText(
    'Implementation Start',
    questionsData,
    personaDetailsData,
  );

  const endDate = getResponseByQuestionText('Time To Value', questionsData, personaDetailsData);

  let readableStartDate = 'N/A';
  let readableEndDate = 'N/A';

  try {
    const options = { includeYear: true, showFullMonth: false };
    readableStartDate = formatDate(startDate, DateFormat.Date, options) || 'N/A';
    readableEndDate = formatDate(endDate, DateFormat.Date, options) || 'N/A';
  } catch (e) {}

  const arr = getResponseByQuestionText('ARR', questionsData, personaDetailsData) || 'N/A';

  const handleEditClick = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/solution-win/${assetId}/edit`);
  }, [assetId, navigation]);

  const onBack = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/solution-win`);
  }, [assetId, navigation]);

  usePageLayout({
    title: pageTitle,
    LeftNode: <TemplatesHeaderNode isPreview={isPreview} title={pageTitle} onBack={onBack} />,
    RightNode:
      isPreview || isMobile ? null : (
        <Stack direction="row" gapX={8}>
          {showApproveButton ? (
            <Button
              StartIcon={AllDoneFill}
              loading={isApproving}
              variant="outlined"
              onClick={onApprove}>
              Approve
            </Button>
          ) : null}
          <Button StartIcon={EditOutline} variant="outlined" onClick={handleEditClick}>
            Edit Solution Win
          </Button>
        </Stack>
      ),
    showHeader: !isMobile,
  });

  const onbackPress = useCallback(() => {
    // navigation.replace(routes.LibraryPortal(), { appendQuery: false });
    navigation.back();
  }, [navigation]);

  const tabs = [
    {
      label: 'Solution Overview',
      value: SolutionWinTab.SOLUTION_OVERVIEW,
    },
    {
      label: 'CX Architecture',
      value: SolutionWinTab.CX_ARCHITECTURE,
    },
    {
      label: 'Competitive Analysis',
      value: SolutionWinTab.COMPETITIVE_ANALYSIS,
    },
    {
      label: 'Customer Insights',
      value: SolutionWinTab.CUSTOMER_INSIGHTS,
    },
    {
      label: 'Collateral',
      value: SolutionWinTab.COLLATERAL,
    },
  ];

  if (isLoading) {
    return (
      <Stack alignItems="center" className="h-[calc(100vh-64px)] w-full" justifyContent="center">
        <Loader size={40} />
      </Stack>
    );
  }

  return (
    <Stack className="w-full" justifyContent="center">
      <Stack
        className="max-w-[101.2rem] min-w-[101.2rem] mx-64 mt-48 mb-128 xs:min-w-full xs:p-12 xs:mt-12"
        direction="col"
        gapY={21}>
        {isAdminRoute ? null : (
          <Stack className="w-full">
            <ArrowBackOutline className="h-24 w-24 cursor-pointer" onClick={onbackPress} />
          </Stack>
        )}
        <Disclaimer className="-mb-[5px]" />
        {/* Header with integrated metadata */}
        <Stack
          alignItems={isMobile ? 'start' : 'center'}
          className="rounded-12 border border-border p-32"
          direction={isMobile ? 'col' : 'row'}
          justifyContent="between">
          <Stack alignItems="center" direction="row" gapX={24} justifyContent="start">
            <Stack>
              <Avatar className={logoStyle} size={100} src={companyLogo} />
            </Stack>

            <Stack className="max-w-[42.5rem]" direction="col" gapY={8}>
              <Typography numberOfLines={1} variant="heading-m">
                {pageTitle || 'N/A'}
              </Typography>

              <Typography className="text-text-secondary" variant="body-short-02">
                {description || 'N/A'}
              </Typography>

              <Typography className="text-text-secondary" variant="body-short-02">
                {productServiceFocus}
              </Typography>
              <Typography className="text-text-secondary" variant="body-short-02">
                {geographicPresence}
              </Typography>
              <Typography className="text-text-secondary" variant="body-short-02">
                {marketPositionSize}
              </Typography>
              <Typography className="text-text-secondary" variant="body-short-02">
                {sizeAndScale}
              </Typography>

              {/* <Typography className="text-text-secondary" variant="body-short-02">
                {`ARR: ${arr}`}
              </Typography> */}
            </Stack>
          </Stack>

          <Stack className="mt-8" direction="col" gapY={8}>
            <Stack alignItems="center" gapX={4}>
              <DepartmentOutline fillColorToken="icon-secondary" height={20} width={20} />
              <Typography className="text-text-secondary" numberOfLines={1} variant="body-short-02">
                {industry || 'N/A'}
              </Typography>
            </Stack>

            <DotSeparator gapX={8}>
              <Tooltip placement="top" title={teamMembersTooltipText}>
                <Stack alignItems="center" gapX={4}>
                  <PeopleGroupOutline fillColorToken="icon-secondary" height={20} width={20} />
                  {formattedTeam}
                </Stack>
              </Tooltip>
              <Stack alignItems="center" gapX={4}>
                <PinOutline fillColorToken="icon-secondary" height={20} width={20} />
                <Typography
                  className="text-text-secondary"
                  numberOfLines={1}
                  variant="body-short-02">
                  {location || 'N/A'}
                </Typography>
              </Stack>
            </DotSeparator>

            <Stack alignItems="center" gapX={4}>
              <Calendar2Outline fillColorToken="icon-secondary" height={20} width={20} />
              <Typography
                className="text-text-secondary"
                numberOfLines={1}
                variant="body-short-02">{`Start: ${readableStartDate}`}</Typography>
            </Stack>

            <Stack alignItems="center" gapX={4}>
              <Calendar2Outline fillColorToken="icon-secondary" height={20} width={20} />
              <Typography
                className="text-text-secondary"
                numberOfLines={1}
                variant="body-short-02">{`End: ${readableEndDate}`}</Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* Tabs */}
        <Stack className="self-start w-full">
          <Tabs tabs={tabs} value={currentTab} onChange={setCurrentTab} />
        </Stack>

        {/* Tab Content */}
        {currentTab === SolutionWinTab.SOLUTION_OVERVIEW && (
          <Stack direction="col" gapY={32}>
            <SolutionOverview
              salesConversations={salesConversations}
              solutionCategories={solutionCategories}
              solutionConversation={solutionConversation}
              solutionType={solutionType}
              solutions={solutions}
            />
          </Stack>
        )}

        {currentTab === SolutionWinTab.CX_ARCHITECTURE && (
          <BusinessImpacts
            actualTime={actualTime}
            aiCapabilities={aiCapabilities}
            blueprintImages={blueprintImages}
            cxLinks={cxLinks}
            painSolved={painSolved}
            predictedTime={predictedTime}
            productsIncluded={productsIncluded}
          />
        )}

        {currentTab === SolutionWinTab.COMPETITIVE_ANALYSIS && (
          <CompetitiveAnalysis
            competitors={competitors}
            differentiators={differentiators}
            winReasons={winReasons}
          />
        )}

        {currentTab === SolutionWinTab.CUSTOMER_INSIGHTS && (
          <CustomerInsights
            customerFeedback={customerFeedback}
            integrations={integrations}
            lessonsLearned={lessonsLearned}
            partnerships={partnerships}
          />
        )}

        {currentTab === SolutionWinTab.COLLATERAL && (
          <Documentations
            images={images}
            links={links}
            solutionWinSalesforceLinks={solutionWinSalesforceLinks}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default SolutionWinDetails;
